import type { NodeKeyHolder } from './types';
import { normalizeSerial } from './normalize';

export type ArmConsequenceSeverity = 'info' | 'warning' | 'error';

export interface ArmConsequence {
    severity: ArmConsequenceSeverity;
    title: string;
    detail: string;
    /** True when the claim will be refused until an operator revokes the incumbent key. */
    blocked: boolean;
}

/**
 * What actually happens if this serial is armed for this node.
 *
 * Design doc §7 decided "arming a new serial for an existing NodeId immediately revokes the
 * previous key". The shipped server does NOT do that. ClaimByHardwareSerialCommandHandler
 * resolves every active key's hardware and returns 409 "HeldByOtherHardware" when one of them
 * belongs to DIFFERENT hardware. Arming is not revocation, and nothing in the arm path touches
 * a live key.
 *
 * "Or unresolvable" used to be in that sentence and is no longer true (#1565). Since #1491 a key
 * whose holder cannot be identified does NOT refuse the claim: the handler yields to the
 * operator's arming, supersedes that key as RevokedForCause, and issues a new one. Those are two
 * different outcomes and this file reported them as one.
 *
 * So this console states the real consequence rather than the documented one. Telling an
 * operator "the previous key will be revoked" would send them away believing a replacement Pi
 * is provisioned, when it is in fact retrying a 409 forever.
 */
export const describeArmConsequence = (
    holder: NodeKeyHolder | null,
    typedSerial: string,
): ArmConsequence | null => {
    if (!holder) return null;

    const serial = normalizeSerial(typedSerial);

    if (holder.activeKeyCount === 0) {
        return {
            severity: 'info',
            title: 'This node has no active key.',
            detail:
                'The hardware can claim as soon as you arm it. Nothing is revoked and nothing is at risk.',
            blocked: false,
        };
    }

    const otherHolders = holder.holderSerials.filter(s => normalizeSerial(s) !== serial);

    if (otherHolders.length > 0) {
        return {
            severity: 'error',
            title: `Node ${holder.nodeId} is already claimed by ${otherHolders.join(', ')}.`,
            detail:
                `Arming does NOT revoke that key. Serial ${serial || '(none entered)'} will be ` +
                'refused with a 409 every time it tries to claim, until you revoke the existing ' +
                'key under Provisioned Devices. Revoke first, then arm.',
            blocked: true,
        };
    }

    // Checked AFTER the mismatch above, deliberately. A node can hold BOTH an
    // unresolvable key and one belonging to different hardware, and the claim path
    // refuses on the second whatever the first says. Reporting "this will go ahead"
    // there would be the same defect this fixes, pointing the other way.
    //
    // Unknown provenance does NOT refuse the claim (#1491), and saying it does was worse than
    // a cosmetic error: it stopped an operator doing something that works, and it hid the one
    // outcome here that destroys a credential outright.
    //
    // ClaimByHardwareSerialCommandHandler: `holderSerial is null` adds the key with
    // holderKnown=false and CONTINUES, because "an operator armed THIS serial for THIS node
    // [is] better evidence than a join that cannot be performed". That key is then revoked
    // RevokedForCause — no rotation grace and no #1489 re-key — so a node still running on it
    // is stranded outright rather than healing itself.
    //
    // Reported as a warning, not a blocker. The operator may well want to do this; they must
    // simply know what it costs.
    if (holder.hasUnresolvableKey) {
        return {
            severity: 'warning',
            title: `Node ${holder.nodeId} holds a key whose hardware cannot be identified.`,
            detail:
                'The claim will go ahead. Your arming is treated as the better evidence, so that ' +
                'key is revoked for cause when this hardware claims — with no grace period and ' +
                'no re-key, unlike a normal rotation. Any node still running on it loses access ' +
                'immediately and cannot recover itself. Revoke it under Provisioned Devices ' +
                'first if you would rather do this deliberately.',
            blocked: false,
        };
    }

    // Every active key resolves to the serial being armed: the supported recovery path.
    //
    // A KNOWN holder is revoked as KeyRevocationKind.Rotated (#1488), not RevokedForCause. The
    // old key keeps authenticating for DeviceApiKey.RotationGrace (24 hours) and can trade
    // itself in through the #1489 re-key route for DeviceApiKey.RekeyWindow (30 days). This
    // used to carry the unknown-holder wording, "stops working immediately ... until it
    // re-claims" (#1540), which overstated the risk and named a recovery the node does not need.
    return {
        severity: 'warning',
        title: `Node ${holder.nodeId} already has a live key issued to this same hardware.`,
        detail:
            'This is the supported recovery path. When this hardware claims again the server ' +
            'rotates its current key and issues a new one. The old key keeps working for 24 hours ' +
            'and stays eligible to re-key for 30 days, so a node still running on it does not ' +
            'lose access and heals itself through the re-key route without re-claiming.',
        blocked: false,
    };
};
